import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { AxiosPublico } from "../../../componentes/axios/Axios";
import Card from '../../../componentes/contenedores/Card';
import GraficoBarra from '../../../componentes/graficos/Barras';
import GraficoLineal from '../../../componentes/graficos/Lineal2';
import FiltroFechaUI from "../../../componentes/metodos/FiltroFechaUI";
import useFechaFiltro from "../../../componentes/metodos/LogicaFiltro";
import Page from '../../../componentes/plantilla/Page';
import {
    AnalisisResumenKPIEmpleado,
    AnalisisTopEmpleados,
    AnalisisTopEmpleadosPedidos,
    AnalisisTendenciasEmpleados,
    AnalisisAlertasPendientes,
    AnalisisErroresRecientes
} from '../../../configuracion/apiUrls';

moment.locale("es");

const EmpleadosAnalisis = () => {
    const [cargando, setCargando] = useState(true);
    const [metricas, setMetricas] = useState(null);
    const [chartTopVentas, setChartTopVentas] = useState(null);
    const [chartTopPedidos, setChartTopPedidos] = useState(null);
    const [chartTendencia, setChartTendencia] = useState(null);
    const [alertas, setAlertas] = useState([]);
    const [errores, setErrores] = useState([]);

    const {
        filtro, setFiltro, fechaSeleccionada, setFechaSeleccionada,
        horaSeleccionada, setHoraSeleccionada, semanaSeleccionada, setSemanaSeleccionada,
        mesSeleccionado, setMesSeleccionado, anioSeleccionado, setAnioSeleccionado,
        obtenerFechasDeFiltro, maxAnio,
    } = useFechaFiltro();

    const cargarTodo = useCallback(async () => {
        setCargando(true);
        const fechas = obtenerFechasDeFiltro();
        const params = {
            fechaInicio: moment(fechas.fechaInicio).format('YYYY-MM-DD HH:mm:ss'),
            fechaFin: moment(fechas.fechaFin).format('YYYY-MM-DD HH:mm:ss'),
        };

        try {
            const [resKPI, resTopV, resTopP, resTend, resAlert, resErr] = await Promise.all([
                AxiosPublico.get(AnalisisResumenKPIEmpleado, { params }),
                AxiosPublico.get(AnalisisTopEmpleados, { params }),
                AxiosPublico.get(AnalisisTopEmpleadosPedidos, { params }),
                AxiosPublico.get(AnalisisTendenciasEmpleados, { params }),
                AxiosPublico.get(AnalisisAlertasPendientes, { params }),
                AxiosPublico.get(AnalisisErroresRecientes, { params })
            ]);

            setMetricas(resKPI.data);

            // 1.Top Ventas 
            if (resTopV.data?.length > 0) {
                setChartTopVentas({
                    labels: resTopV.data.map(e => e.Empleado?.nombreCompleto || "N/A"),
                    data: resTopV.data.map(e => parseFloat(e.totalVendido)),
                    colors: resTopV.data.map((_, i) => `hsla(${200 + (i * 20)}, 70%, 50%, 0.8)`),
                    titulo: "Top 10 Empleados por Ventas"
                });
            } else { setChartTopVentas(null); }

            // 2.Top Pedidos
            if (resTopP.data?.length > 0) {
                setChartTopPedidos({
                    labels: resTopP.data.map(e => e.Empleado?.nombreCompleto || "N/A"),
                    data: resTopP.data.map(e => parseFloat(e.pedidosProcesados)),
                    colors: resTopP.data.map((_, i) => `hsla(${160 + (i * 20)}, 70%, 50%, 0.8)`),
                    titulo: "Top 10 Empleados por Pedidos"
                });
            } else { setChartTopPedidos(null); }

            // 3. Tendencia de Actividad 
            if (resTend.data?.pedidos?.length > 0) {
                setChartTendencia({
                    labels: resTend.data.pedidos.map(d => moment(d.fecha).format("DD/MM")),
                    datasets: [
                        {
                            label: "Pedidos Realizados",
                            data: resTend.data.pedidos.map(d => parseFloat(d.cantidad || 0)),
                            borderColor: '#4e73df',
                            backgroundColor: 'rgba(78, 115, 223, 0.1)',
                            fill: true,
                            tension: 0.3,
                            pointRadius: 5,
                            pointBackgroundColor: '#4e73df'
                        },
                        {
                            label: "Compras Realizadas",
                            data: resTend.data.compras?.map(d => parseFloat(d.cantidad || 0)) || [],
                            borderColor: '#1cc88a',
                            backgroundColor: 'rgba(28, 200, 138, 0.1)',
                            fill: true,
                            tension: 0.3,
                            pointRadius: 5,
                            pointBackgroundColor: '#1cc88a'
                        }
                    ],
                    titulo: "Tendencia de Actividad"
                });
            } else {
                setChartTendencia(null);
            }

            setAlertas(resAlert.data);
            setErrores(resErr.data);

        } catch (error) {
            console.error("Error al cargar datos de empleados:", error);
        } finally {
            setCargando(false);
        }
    }, [obtenerFechasDeFiltro]);

    useEffect(() => { cargarTodo(); }, []);

    const datos = {
        titulo: {
            titulo: "Análisis de Rendimiento",
            url: "/app/admin/analisis/empleados",
            tituloUrl: "Empleados",
            nombreUrl: "Rendimiento Global",
        },
    };

    return (
        <Page datos={datos}>
            <Card>
                <FiltroFechaUI
                    filtro={filtro} setFiltro={setFiltro}
                    fechaSeleccionada={fechaSeleccionada} setFechaSeleccionada={setFechaSeleccionada}
                    horaSeleccionada={horaSeleccionada} setHoraSeleccionada={setHoraSeleccionada}
                    semanaSeleccionada={semanaSeleccionada} setSemanaSeleccionada={setSemanaSeleccionada}
                    mesSeleccionado={mesSeleccionado} setMesSeleccionado={setMesSeleccionado}
                    anioSeleccionado={anioSeleccionado} setAnioSeleccionado={setAnioSeleccionado}
                    handleApplyFilter={cargarTodo}
                    maxAnio={maxAnio}
                />
                <hr />
                {cargando ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-2">Cargando análisis...</p>
                    </div>
                ) : (
                    <>
                        {/* KPIs de Resumen */}
                        <div className="row mb-4">
                            <div className="col-md-4 mb-3">
                                <div className="p-3 border-start border-primary border-4 bg-light rounded shadow-sm">
                                    <h6 className="text-muted small text-uppercase fw-bold">Empleados Activos</h6>
                                    <h3 className="fw-bold mb-0">{metricas?.totalEmpleadosActivos || 0}</h3>
                                </div>
                            </div>
                            <div className="col-md-4 mb-3">
                                <div className="p-3 border-start border-success border-4 bg-light rounded shadow-sm">
                                    <h6 className="text-muted small text-uppercase fw-bold">Promedio Pedidos</h6>
                                    <h3 className="fw-bold mb-0">{metricas?.promedioPedidosPorEmpleado || 0}</h3>
                                </div>
                            </div>
                            <div className="col-md-4 mb-3">
                                <div className="p-3 border-start border-danger border-4 bg-light rounded shadow-sm">
                                    <h6 className="text-muted small text-uppercase fw-bold">Tasa de Error</h6>
                                    <h3 className="fw-bold mb-0 text-danger">{metricas?.tasaErrorPedidos || "0%"}</h3>
                                </div>
                            </div>
                        </div>

                        {/* Gráficos de Barras */}
                        <div className="row mb-5">
                            <div className="col-lg-6 mb-4">
                                {chartTopVentas && <GraficoBarra datos={chartTopVentas} esMonetario={true} />}
                            </div>
                            <div className="col-lg-6 mb-4">
                                {chartTopPedidos && <GraficoBarra datos={chartTopPedidos} esMonetario={false} />}
                            </div>
                        </div>

                        {/* Gráfico Lineal de Tendencia */}
                        <div className="row mb-5">
                            <div className="col-12">
                                <div className="p-4 bg-white shadow-sm rounded border">
                                    {chartTendencia && <GraficoLineal datos={chartTendencia} />}
                                </div>
                            </div>
                        </div>

                        {/* Alertas y Errores */}
                        <div className="row">
                            <div className="col-lg-5 mb-4">
                                <h6 className="fw-bold"><i className="fas fa-exclamation-triangle text-warning mx-2"></i>Pedidos Pendientes</h6>
                                <ul className="list-group shadow-sm">
                                    {alertas.map((a, i) => (
                                        <li key={i} className="list-group-item d-flex justify-content-between align-items-center">
                                            {a.urgencia}
                                            <span className="badge bg-primary rounded-pill">{a.total}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="col-lg-7 mb-4">
                                <h6 className="fw-bold"><i className="fas fa-history text-danger mx-2"></i>Últimos Errores de Registro</h6>
                                <div className="table-responsive border rounded shadow-sm">
                                    <table className="table table-sm table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Empleado</th>
                                                <th>Estado</th>
                                                <th>Fecha</th>
                                                <th>Pedido</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {errores.map((e, i) => (
                                                <tr key={i}>
                                                    <td className="small">{e.empleado}</td>
                                                    <td><span className="badge bg-danger">Anulada</span></td>
                                                    <td className="small">{moment(e.fecha).format('DD/MM/YYYY')}</td>
                                                    <td className="fw-bold text-center">#{e.pedidoId}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </Card>
        </Page>
    );
};

export default EmpleadosAnalisis;